/*
  themedSwal.js  (hosted payment page, src/utils/themedSwal.js)
  ------------------------------------------------------------
  SweetAlert2, themed from the embedded theme (no React context needed).
  Theme is read synchronously via getWqTheme() (URL/sessionStorage/<html>).

  Three ways to use it, pick whichever fits the call site:

  1) IMPORT SWAP (least edits) - change only the import line, keep every
     existing Swal.fire(...) call exactly as-is:
        import { WqSwal as Swal } from "../../utils/themedSwal";
        ...
        Swal.fire({ title: "Done", icon: "success" });

  2) DROP-IN FUNCTION - supports both Swal.fire signatures:
        import { themedSwal } from "../../utils/themedSwal";
        themedSwal({ title: "Done", icon: "success" });
        themedSwal("Done", "All good", "success");

  3) HOOK (parity with the dashboard's useThemeSwal):
        const { showSwal } = useThemeSwal();
        showSwal({ title: "Done", icon: "success" });

  buttonsStyling is left ON, so buttons are coloured via confirm/cancel colours
  with no extra CSS. (The dashboard turned it off and relied on global button
  classes; here we stay self-contained.)
*/

import Swal from "sweetalert2";
import { getWqTheme } from "./selectTheme";

const themeOptions = () => {
  const isDark = getWqTheme() === "dark";
  return {
    background: isDark ? "#1f2937" : "#ffffff",
    color: isDark ? "#f3f4f6" : "#1f2937",
    confirmButtonColor: "#FF6700",
    cancelButtonColor: isDark ? "#ef4444" : "#dc2626",
    backdrop: `rgba(0, 0, 0, ${isDark ? "0.8" : "0.4"})`,
    // Hook for the optional wq-swal.css (only needed if a dialog uses inputs).
    customClass: { popup: isDark ? "wq-swal-dark" : "wq-swal-light" },
  };
};

/*
  Mixin baked once at import. The hosted page's theme is fixed for the session
  (it comes from the URL), so a single evaluation is correct and cheapest.
  WqSwal.fire merges these defaults with each call's own options.
*/
export const WqSwal = Swal.mixin(themeOptions());

// Dynamic drop-in for Swal.fire, supporting both call signatures.
export const themedSwal = (optionsOrTitle, text, icon) => {
  const base = themeOptions();
  if (typeof optionsOrTitle === "string" || optionsOrTitle == null) {
    return Swal.fire({ ...base, title: optionsOrTitle, text, icon });
  }
  return Swal.fire({ ...base, ...optionsOrTitle });
};

// Parity with the dashboard hook API.
export const useThemeSwal = () => ({ showSwal: themedSwal });

export default themedSwal;